import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Coffee, Lock, Eye, EyeOff, CheckCircle, AlertTriangle } from 'lucide-react';
import api from '../../api/axios';
import toast from 'react-hot-toast';

function ResetPasswordPage() {
  // The email link is /reset-password?token=…; /reset-password/:token is accepted too
  const params = useParams();
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('token') || params.token || '').trim();
  const navigate = useNavigate();

  // Checking the link is read-only on the server (safe for mail-app link previews)
  const [checking, setChecking] = useState(true);
  const [linkProblem, setLinkProblem] = useState(null); // { reason, message, email }

  useEffect(() => {
    if (!token) {
      setLinkProblem({ reason: 'invalid', message: 'This reset link is invalid. Please request a new one.' });
      setChecking(false);
      return;
    }
    let cancelled = false;
    api.get(`/auth/reset-password/${encodeURIComponent(token)}`)
      .then((res) => {
        if (cancelled) return;
        if (!res.data.valid) setLinkProblem(res.data);
      })
      .catch(() => {
        // Network trouble: let the user try; the POST reports the real state
      })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [token]);

  const requestNewLink = () => {
    const email = linkProblem?.email;
    navigate(email ? `/forgot-password?email=${encodeURIComponent(email)}` : '/forgot-password');
  };

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!password || !confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setSuccess(true);
      toast.success('Password reset successfully!');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      const data = err.response?.data;
      if (data?.reason) setLinkProblem(data);
      else setError(data?.error || data?.errors?.[0]?.msg || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, var(--primary-light) 0%, var(--primary) 50%, var(--primary-dark) 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div
        style={{
          background: 'var(--white)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-lg)',
          padding: '40px',
          width: '100%',
          maxWidth: '420px',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: 'var(--primary)',
              marginBottom: '12px',
            }}
          >
            <Coffee size={28} color="var(--text)" />
          </div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)', marginBottom: '4px' }}>
            New Password
          </h1>
          <p style={{ color: 'var(--text-light)', fontSize: '0.875rem' }}>
            Choose a strong password for your account.
          </p>
        </div>

        {checking ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
            <div className="spinner" />
          </div>
        ) : linkProblem ? (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <AlertTriangle size={44} color="var(--warning)" style={{ marginBottom: '14px' }} />
            <h3 style={{ fontWeight: 600, marginBottom: '8px' }}>
              {{
                expired: 'Link expired',
                used: 'Link already used',
                replaced: 'Link replaced',
              }[linkProblem.reason] || 'Invalid link'}
            </h3>
            <p style={{ color: 'var(--text-light)', fontSize: '0.875rem', marginBottom: '22px' }}>
              {linkProblem.message || linkProblem.error}
            </p>
            <button
              type="button"
              className="btn btn-primary w-full btn-lg"
              style={{ justifyContent: 'center', marginBottom: '16px' }}
              onClick={requestNewLink}
            >
              Request a new link
            </button>
            <Link to="/login" style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>
              Back to login
            </Link>
          </div>
        ) : success ? (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <CheckCircle size={48} color="var(--success)" style={{ marginBottom: '16px' }} />
            <h3 style={{ fontWeight: 600, marginBottom: '8px' }}>Password updated!</h3>
            <p style={{ color: 'var(--text-light)', fontSize: '0.875rem', marginBottom: '24px' }}>
              Redirecting you to login…
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <div
                style={{
                  background: 'var(--danger-light)',
                  color: 'var(--danger)',
                  padding: '10px 14px',
                  borderRadius: 'var(--radius)',
                  fontSize: '0.875rem',
                  marginBottom: '16px',
                  border: '1px solid #ffcdd2',
                }}
              >
                {error}
              </div>
            )}

            <div className="form-group">
              <label className="form-label" htmlFor="password">New password</label>
              <div style={{ position: 'relative' }}>
                <Lock
                  size={16}
                  style={{
                    position: 'absolute', left: '12px', top: '50%',
                    transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none',
                  }}
                />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ paddingLeft: '38px', paddingRight: '40px' }}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  style={{
                    position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px',
                  }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="confirm">Confirm new password</label>
              <div style={{ position: 'relative' }}>
                <Lock
                  size={16}
                  style={{
                    position: 'absolute', left: '12px', top: '50%',
                    transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none',
                  }}
                />
                <input
                  id="confirm"
                  type={showConfirm ? 'text' : 'password'}
                  className="form-input"
                  placeholder="Repeat your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={{ paddingLeft: '38px', paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  style={{
                    position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px',
                  }}
                >
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary w-full btn-lg"
              disabled={loading}
              style={{ justifyContent: 'center', marginBottom: '16px' }}
            >
              {loading ? (
                <>
                  <div className="spinner" style={{ width: '18px', height: '18px', borderWidth: '2px' }} />
                  Updating…
                </>
              ) : (
                'Set new password'
              )}
            </button>

            <div style={{ textAlign: 'center' }}>
              <Link to="/login" style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>
                Back to login
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default ResetPasswordPage;
